import * as React from "react"
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "framer-motion"

import { cn } from "../lib/utils"

const DEFAULT_SIZE = 40
const DEFAULT_MAGNIFICATION = 56
const DEFAULT_DISTANCE = 120
const MotionDiv = motion.div

const Dock = React.forwardRef(({
  className,
  children,
  iconSize = DEFAULT_SIZE,
  iconMagnification = DEFAULT_MAGNIFICATION,
  disableMagnification = false,
  iconDistance = DEFAULT_DISTANCE,
  direction = "bottom",
  onMouseMove,
  onMouseLeave,
  ...props
}, ref) => {
  const mouseX = useMotionValue(Infinity)
  const reduceMotion = useReducedMotion()

  const renderedChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child) || child.type !== DockIcon) return child

    return React.cloneElement(child, {
      ...child.props,
      mouseX,
      size: iconSize,
      magnification: iconMagnification,
      disableMagnification: disableMagnification || reduceMotion,
      distance: iconDistance,
    })
  })

  return (
    <MotionDiv
      ref={ref}
      className={cn(
        "mx-auto flex w-max justify-center gap-[var(--ds-space-1)] rounded-[var(--ds-radius-surface)] border border-border bg-card p-[var(--ds-space-2)] shadow-[var(--shadow-popover)]",
        direction === "top" && "items-start",
        direction === "middle" && "items-center",
        direction === "bottom" && "items-end",
        className,
      )}
      onMouseMove={(event) => {
        mouseX.set(event.clientX)
        onMouseMove?.(event)
      }}
      onMouseLeave={(event) => {
        mouseX.set(Infinity)
        onMouseLeave?.(event)
      }}
      {...props}
    >
      {renderedChildren}
    </MotionDiv>
  )
})
Dock.displayName = "Dock"

const DockIcon = ({
  size = DEFAULT_SIZE,
  magnification = DEFAULT_MAGNIFICATION,
  disableMagnification = false,
  distance = DEFAULT_DISTANCE,
  mouseX,
  className,
  children,
  style,
  ...props
}) => {
  const ref = React.useRef(null)
  const defaultMouseX = useMotionValue(Infinity)

  const distanceFromPointer = useTransform(mouseX ?? defaultMouseX, (pointerX) => {
    const bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 }
    return pointerX - bounds.x - bounds.width / 2
  })

  const sizeTransform = useTransform(
    distanceFromPointer,
    [-distance, 0, distance],
    [size, disableMagnification ? size : magnification, size],
  )

  const animatedSize = useSpring(sizeTransform, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  })

  return (
    <MotionDiv
      ref={ref}
      className={cn(
        "flex aspect-square shrink-0 items-center justify-center rounded-[var(--ds-radius-control)]",
        className,
      )}
      style={{ ...style, width: animatedSize, height: animatedSize }}
      {...props}
    >
      {children}
    </MotionDiv>
  )
}
DockIcon.displayName = "DockIcon"

export { Dock, DockIcon }
